import { useQuery } from "react-query";
import { ServerError, UnauthorizedError } from "../shared/utils";
import log from "../shared/utils/log";

const fetchExportReasons = async ({
  accountId,
  token,
}: {
  accountId: string;
  token: () => Promise<string>;
}): Promise<unknown[]> => {
  const tokenValue = await token();
  const url = `/api/${accountId}/config/exportReasons`;

  const headers = new Headers({
    Authorization: `Bearer ${tokenValue}`,
    "Content-Type": "application/json",
    Accept: "application/json",
  });

  const response = await fetch(url, {
    method: "GET",
    headers,
  });

  if (!response.ok) {
    if (response.status === 401) {
      console.warn("401 Unauthorized when fetching export reasons");
      throw new UnauthorizedError("Unauthorized access - 401 error.");
    }

    log(`Server error ${response.status} when fetching export reasons`);
    throw new ServerError(`Server error ${response.status}`);
  }

  let data: unknown;
  try {
    data = await response.json();
  } catch (err) {
    console.error("Invalid JSON response from server:", err);
    throw new Error("Invalid JSON response from server.");
  }

  if (!Array.isArray(data)) {
    log("Expected array but got:", data);
    return [];
  }

  return data;
};

export const useExportReasons = ({
  accountId,
  token,
}: {
  accountId: string;
  token?: () => Promise<string>;
}) => {
  if (!token) {
    throw new Error("Authentication token function is missing.");
  }

  return useQuery(
    ["exportReasons", accountId],
    () => fetchExportReasons({ accountId, token }),
    {
      staleTime: 5 * 60 * 1000,
      enabled: !!accountId && !!token,
      retry: (failureCount, error) => {
        // Do NOT retry for UnauthorizedError or ServerError
        return !(
          error instanceof UnauthorizedError || error instanceof ServerError
        );
      },
    },
  );
};
